import { isAxiosError } from 'axios';
import { useCallback } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';
import { captureException } from '~/hooks/useExceptionLogger';
import { DocumentBuilderForm } from '~/types/document-builder';
import { JSONApiErrorResponse } from '~/types/jsonapi';

interface ErrorMapper {
  regExp: RegExp;
  field:
    | FieldPath<DocumentBuilderForm>
    | `root.${string}`
    | ((index: number) => FieldPath<DocumentBuilderForm> | `root.${string}`);
}

const mappers: ErrorMapper[] = [
  {
    regExp: /cedentePrestatore\/datiAnagrafici\/idFiscaleIva\/idCodice/g,
    field: 'counterpart.vatNumber',
  },
  {
    regExp: /cessionarioCommittente\/sede\/cap/g,
    field: 'counterpart.postalCode',
  },
  {
    regExp: /cessionarioCommittente\/sede\/comune/g,
    field: 'counterpart.city',
  },
  {
    regExp: /cessionarioCommittente\/sede\/indirizzo/g,
    field: 'counterpart.address',
  },
  {
    regExp: /cessionarioCommittente\/sede\/nazione/g,
    field: 'counterpart.addressCountry',
  },
  {
    regExp: /cessionarioCommittente\/sede\/provicina/g,
    field: 'counterpart.provinceCode',
  },
  {
    regExp: /cessionarioCommittente\/datiAnagrafici\/anagrafica\/denominazione/g,
    field: 'counterpart.companyName',
  },
  {
    regExp: /codiceDestinatario/g,
    field: 'counterpart.destinationCode',
  },
  {
    regExp: /cessionarioCommittente\/datiAnagrafici\/anagrafica\/nome/g,
    field: 'counterpart',
  },
  {
    regExp: /cessionarioCommittente\/datiAnagrafici\/anagrafica\/cognome/g,
    field: 'counterpart',
  },
  {
    regExp: /cessionarioCommittente\/datiAnagrafici\/anagrafica\/titolo/g,
    field: 'counterpart',
  },
  {
    regExp: /cessionarioCommittente\/datiAnagrafici\/anagrafica\/codEori/g,
    field: 'counterpart',
  },
  {
    regExp: /cessionarioCommittente\/datiAnagrafici\/idFiscaleIva\/idPaese/g,
    field: 'counterpart.country',
  },
  {
    regExp: /cessionarioCommittente\/datiAnagrafici\/idFiscaleIva\/idCodice/g,
    field: 'counterpart.vatNumber',
  },
  {
    regExp: /cessionarioCommittente\/datiAnagrafici\/codiceFiscale/g,
    field: 'counterpart.taxNumber',
  },
  {
    regExp: /datiBeniServizi\/dettaglioLinee\/(\d+)\/prezzoUnitario/g,
    field: (index) => `items.${index}.netPrice`,
  },
  {
    regExp: /datiBeniServizi\/dettaglioLinee\/(\d+)\/prezzoTotale/g,
    field: (index) => `items.${index}`,
  },
  {
    regExp: /datiPagamento\/\d+\/dettaglioPagamento\/(\d+)\/importoPagamento/g,
    field: (index) => `flows.${index}.amount`,
  },
  {
    regExp: /datiBeniServizi\/datiRiepilogo\/(\d+)\/imposta/g,
    field: (index) => `root.summaryVatAmount${index}`,
  },
  {
    regExp: /datiBeniServizi\/datiRiepilogo\/(\d+)\/imponibileImporto/g,
    field: (index) => `root.summaryBeforeTax${index}`,
  },
  {
    regExp: /datiGeneraliDocumento\/data/g,
    field: 'date',
  },
  {
    regExp: /datiGeneraliDocumento\/causale/g,
    field: 'motivation',
  },
  {
    regExp: /datiGeneraliDocumento\/divisa/g,
    field: 'currency',
  },
  {
    regExp: /importoTotaleDocumento/g,
    field: 'root.documentTotal',
  },
  {
    regExp: /datiGeneraliDocumento\/numero/g,
    field: 'root.number',
  },
  {
    regExp: /datiGeneraliDocumento\/tipoDocumento/g,
    field: 'subtype',
  },
  {
    regExp: /datiRitenuta\/(\d+)\/tipoRitenuta/g,
    field: (index: number) => `withholdingTax.${index}.type`,
  },
  {
    regExp: /datiRitenuta\/(\d+)\/importoRitenuta/g,
    field: (index: number) => `withholdingTax.${index}.percentage`,
  },
  {
    regExp: /datiRitenuta\/(\d+)\/aliquotaRitenuta/g,
    field: (index: number) => `withholdingTax.${index}.percentage`,
  },
  {
    regExp: /datiRitenuta\/(\d+)\/causalePagamento/g,
    field: (index: number) => `withholdingTax.${index}.motivation`,
  },
  {
    regExp: /dettaglioPagamento\/(\d+)\/accountId/g,
    field: 'paymentInfo.accountId',
  },
  {
    regExp: /dettaglioPagamento\/(\d+)\/iban/g,
    field: 'paymentInfo.paymentAccount',
  },
  {
    regExp: /dettaglioPagamento\/(\d+)\/modalitaPagamento/g,
    field: 'paymentInfo.paymentMethod',
  },
  {
    regExp: /dettaglioPagamento\/(\d+)\/importoPagamento/g,
    field: (index: number) => `flows.${index}.amount`,
  },
  {
    regExp: /dettaglioPagamento\/(\d+)\/dataScadenzaPagamento/g,
    field: (index: number) => `flows.${index}.expectedPaymentDate`,
  },
  {
    regExp: /cedentePrestatore\/datiAnagrafici\/anagrafica/g,
    field: 'root.denominazione',
  },
  {
    regExp: /cedentePrestatore\/sede\/cap/g,
    field: 'root.cap',
  },
  {
    regExp: /cedentePrestatore\/sede\/comune/g,
    field: 'root.comune',
  },
  {
    regExp: /cedentePrestatore\/sede\/indirizzo/g,
    field: 'root.indirizzo',
  },
  {
    regExp: /cedentePrestatore\/sede\/nazione/g,
    field: 'root.nazione',
  },
  {
    regExp: /cedentePrestatore\/sede\/numeroCivico/g,
    field: 'root.numeroCivico',
  },
  {
    regExp: /cedentePrestatore\/sede\/provincia/g,
    field: 'root.provincia',
  },
];

export const useMapResponseErrors = () => {
  const { setError } = useFormContext<DocumentBuilderForm>();

  const mapResponseErrors = useCallback(
    (responseError: Error) => {
      if (isAxiosError(responseError)) {
        const errors = (responseError.response?.data as JSONApiErrorResponse | undefined)?.errors ?? [];

        errors.forEach((error) => {
          const matched = mappers.some(({ field, regExp }) => {
            if (!error.source) {
              return false;
            }
            const match = regExp.exec(error.source.pointer);

            if (match) {
              const index = parseInt(match[1]!);
              if (typeof field === 'string') {
                setError(field, { message: error.detail, type: 'validate' });
              } else {
                setError(field(index), { message: error.detail, type: 'validate' });
              }
              return true;
            }
          });
          if (!matched) {
            captureException(responseError, { extra: { invoiceErrors: errors } });
            console.error('Unmapped error', error.source?.pointer);
          }
        });
      }
    },
    [setError],
  );
  return mapResponseErrors;
};
